import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useOrganizationContext } from './OrganizationContext';
import { YearType } from '../services/types';

interface YearTypeContextValue {
  yearType: YearType;
  isLoading: boolean;
}

const YearTypeContext = createContext<YearTypeContextValue | undefined>(undefined);

export const useYearTypeContext = (): YearTypeContextValue => {
  const context = useContext(YearTypeContext);
  if (!context) {
    throw new Error('useYearTypeContext must be used within a YearTypeProvider');
  }
  return context;
};

export const YearTypeProvider = ({ children }: { children: ReactNode }) => {
  const { locationId } = useOrganizationContext();
  const [yearType, setYearType] = useState<YearType>('q1');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchYearType = async () => {
      try {
        const response = await fetch(import.meta.env.VITE_LOCATIONS_API_URL);
        const locations = await response.json();

        const matchingLocation = locations.find((loc: any) => loc.locationId === locationId);
        const fetchedYearType = matchingLocation?.breReportingTool?.yearType;

        if (fetchedYearType === 'q1' || fetchedYearType === 'q2' || fetchedYearType === 'q3' || fetchedYearType === 'q4') {
          setYearType(fetchedYearType);
        } else {
          setYearType('q1'); // default fallback
        }
      } catch (error) {
        console.error('Error fetching yearType from location ID:', error);
        setYearType('q1');
      } finally {
        setIsLoading(false);
      }
    };

    if (locationId) {
      fetchYearType();
    }
  }, [locationId]);

  return (
    <YearTypeContext.Provider value={{ yearType, isLoading }}>
      {children}
    </YearTypeContext.Provider>
  );
};
